// Apex Infinity Intelligence — Round 10 Corporate Master View (final)
// Completely separate experience. Never visible to subscribing-company accounts.
// Mount: window.location.hash === '#corporate' OR window.location.host starts with 'corporate.'
import React, { useEffect, useState, useCallback, useMemo } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const CORP_TOKEN_KEY = "apex_corp_token";
const auth = () => { const t = localStorage.getItem(CORP_TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } })
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), { data, status: r.status }); return data; });

const corpBg = "#0a0a0f";
const corpAccent = "#dc2626";
const corpCyan = "#06b6d4";
const card = { background: "#13131c", border: "1px solid #2a2a3a", borderRadius: 8, padding: 18, marginBottom: 14, color: "#e5e7eb" };
const subhead = { fontFamily: "Rajdhani, Inter, sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 10, letterSpacing: 1, color: "#94a3b8", textTransform: "uppercase" };
const inputStyle = { padding: "8px 10px", background: "#0a0a0f", border: "1px solid #2a2a3a", borderRadius: 4, color: "#e5e7eb", fontSize: 12, fontFamily: "monospace" };
const btn = { padding: "8px 14px", border: "1px solid " + corpAccent, background: corpAccent, color: "#fff", fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: "pointer", letterSpacing: 0.5 };
const btnGhost = { ...btn, background: "transparent", borderColor: "#2a2a3a", color: "#94a3b8" };
const pill = (bg, text = "#fff") => ({ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, letterSpacing: 0.5, background: bg, color: text });
const tableHead = { fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "#94a3b8", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #2a2a3a" };
const tableCell = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #2a2a3a" };

const dollars = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CorpLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setBusy(true);
    try {
      const r = await j("/api/iv/r10/corp/login", { method: "POST", body: JSON.stringify({ email, password }) });
      localStorage.setItem(CORP_TOKEN_KEY, r.token);
      onLogin(r.user);
    } catch (e2) { setErr(e2.message || "Login failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="corp-login" style={{ minHeight: "100vh", background: corpBg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <form onSubmit={submit} style={{ ...card, width: 400, marginBottom: 0 }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: 2, color: corpAccent }}>APEX INFINITY</div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: "#94a3b8", marginTop: 4 }}>CORPORATE MASTER VIEW</div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ ...subhead, marginBottom: 4, display: "block" }}>Corporate Email</label>
          <input data-testid="corp-login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...subhead, marginBottom: 4, display: "block" }}>Password</label>
          <input data-testid="corp-login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
        </div>
        {err && <div style={{ background: "#7f1d1d", color: "#fff", padding: 8, borderRadius: 4, fontSize: 12, marginBottom: 10 }} data-testid="corp-login-error">{err}</div>}
        <button data-testid="corp-login-submit" type="submit" disabled={busy} style={{ ...btn, width: "100%" }}>
          {busy ? "Authenticating…" : "Sign In"}
        </button>
        <div style={{ fontSize: 10, color: "#475569", marginTop: 14, textAlign: "center" }}>
          Restricted access. Corporate credentials only.<br/>All activity is logged.
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, color = "#e5e7eb" }) {
  return (
    <div style={{ background: "#0a0a0f", padding: 14, borderRadius: 6, border: "1px solid #2a2a3a" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color, fontFamily: "Rajdhani, sans-serif" }}>{value || "—"}</div>
    </div>
  );
}

function SubscribersPage({ onView }) {
  const [data, setData] = useState({ subscribers: [], count: 0 });
  const [q, setQ] = useState("");
  const [planF, setPlanF] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (planF) p.set("plan", planF);
    j(`/api/iv/r10/corp/subscribers?${p.toString()}`).then(setData).catch(() => {});
  }, [q, planF]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, label) => {
    const reason = window.prompt(`${label} — reason (logged in master audit):`, "");
    if (reason === null) return;
    try {
      await j(`/api/iv/r10/corp/billing/${path}`, { method: "POST", body: JSON.stringify({ ...body, reason }) });
      alert(`${label} done.`); load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={subhead}>Subscribers · {data.count}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="corp-sub-search" placeholder="Search name / id" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle, width: 200 }} />
            <select data-testid="corp-sub-plan" value={planF} onChange={e => setPlanF(e.target.value)} style={{ ...inputStyle, width: 160 }}>
              <option value="">All plans</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>
      <div style={card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={tableHead}>Company</th><th style={tableHead}>Plan</th><th style={tableHead}>Status</th>
                <th style={tableHead}>Seats</th><th style={tableHead}>IntelliVoice</th><th style={tableHead}>ARM</th>
                <th style={tableHead}>FieldProIQ</th><th style={tableHead}>MRR</th><th style={tableHead}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.subscribers.map((s, i) => (
                <tr key={s.id || i} data-testid={`corp-sub-row-${s.id || i}`}>
                  <td style={tableCell}>
                    <strong>{s.name}</strong>
                    {s.suspended && <span style={{ ...pill(corpAccent), marginLeft: 6 }}>SUSPENDED</span>}
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{s.id}</div>
                  </td>
                  <td style={tableCell}><span style={pill("#1e40af")}>{(s.plan || "—").toUpperCase()}</span></td>
                  <td style={tableCell}><span style={pill(s.billing_status === "active" ? "#16a34a" : s.billing_status === "past_due" ? "#dc2626" : "#6b7280")}>{s.billing_status}</span></td>
                  <td style={tableCell}>{s.seat_used} / {s.seat_limit ?? "∞"}</td>
                  <td style={tableCell}>{s.intellivoice_subscribed ? "✅" : "—"}</td>
                  <td style={tableCell}>{s.arm_connected ? "✅" : "—"}</td>
                  <td style={tableCell}>{s.fieldproiq_connected ? "✅" : "—"}</td>
                  <td style={tableCell}>{dollars(s.monthly_revenue_cents)}</td>
                  <td style={tableCell}>
                    <button style={{ ...btnGhost, padding: "4px 8px", fontSize: 10 }} onClick={() => onView(s.id)} data-testid={`corp-sub-view-${s.id || i}`}>View</button>
                    {s.suspended
                      ? <button style={{ ...btn, padding: "4px 8px", fontSize: 10, marginLeft: 4 }} onClick={() => act("reactivate", { company_id: s.id }, "Reactivate")} data-testid={`corp-sub-reactivate-${s.id}`}>Reactivate</button>
                      : <button style={{ ...btn, padding: "4px 8px", fontSize: 10, marginLeft: 4 }} onClick={() => act("suspend", { company_id: s.id }, "Suspend")} data-testid={`corp-sub-suspend-${s.id}`}>Suspend</button>}
                  </td>
                </tr>
              ))}
              {!data.subscribers.length && <tr><td colSpan={9} style={{ ...tableCell, textAlign: "center", color: "#94a3b8" }}>No subscribers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompanyDetail({ companyId, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    j(`/api/iv/r10/corp/subscribers/${companyId}`).then(setData).catch(() => setData({ error: true }));
  }, [companyId]);

  const billingAct = async (path, body, label) => {
    const reason = window.prompt(`${label} — reason (logged):`, "");
    if (reason === null) return;
    try {
      await j(`/api/iv/r10/corp/billing/${path}`, { method: "POST", body: JSON.stringify({ ...body, reason }) });
      alert(`${label} ok`);
    } catch (e) { alert(e.message); }
  };

  if (!data) return <div style={{ ...card, color: "#94a3b8" }}>Loading…</div>;
  if (data.error) return <div style={{ ...card }}>Failed to load.</div>;
  const c = data.company || {};
  return (
    <div data-testid="corp-company-detail">
      <button style={btnGhost} onClick={onBack} data-testid="corp-detail-back">← Back to subscribers</button>
      <div style={card}>
        <div style={subhead}>Company · {c.legal_name || c.name || c.id}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginTop: 8 }}>
          <Stat label="Team" value={data.team_count} />
          <Stat label="ARM Open" value={data.arm_open} color={corpCyan} />
          <Stat label="Plan" value={(c.plan || "—").toUpperCase()} />
          <Stat label="Suspended" value={c.suspended ? "Yes" : "No"} color={c.suspended ? corpAccent : "#16a34a"} />
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: "#94a3b8" }}>
          <strong>Read-only support view.</strong> Corporate cannot edit subscriber data. All views logged.
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Billing Management</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btn} onClick={() => { const d = +(window.prompt("Extend trial by N days (1-90):", "14") || 0); if (d) billingAct("extend-trial", { company_id: c.id, days: d }, "Extend trial"); }} data-testid="corp-extend-trial">Extend Trial</button>
          <button style={btn} onClick={() => { const a = +(window.prompt("Credit amount (USD):", "100") || 0); if (a) billingAct("credit", { company_id: c.id, amount_cents: Math.round(a * 100) }, "Apply credit"); }} data-testid="corp-credit">Apply Credit</button>
          <button style={btn} onClick={() => { const p = window.prompt("New plan (starter/professional/enterprise):", "professional"); if (p) billingAct("plan", { company_id: c.id, new_plan: p }, "Change plan"); }} data-testid="corp-change-plan">Change Plan</button>
          <button style={btn} onClick={() => { const p = parseFloat(window.prompt("New revenue share % (0-50):", "2") || ""); if (!isNaN(p)) billingAct("rev-share-percent", { company_id: c.id, percent: p }, "Revenue share %"); }} data-testid="corp-rev-share">Adjust Rev Share %</button>
          {c.suspended
            ? <button style={btn} onClick={() => billingAct("reactivate", { company_id: c.id }, "Reactivate")} data-testid="corp-reactivate">Reactivate</button>
            : <button style={btn} onClick={() => billingAct("suspend", { company_id: c.id }, "Suspend")} data-testid="corp-suspend">Suspend</button>}
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Recent Payments</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>Kind</th><th style={tableHead}>Amount</th><th style={tableHead}>Status</th><th style={tableHead}>When</th><th style={tableHead}></th></tr></thead>
          <tbody>
            {(data.recent_payments || []).map(p => (
              <tr key={p.id}>
                <td style={tableCell}>{p.kind || "plan"}</td>
                <td style={tableCell}>{dollars(p.amount_cents)}</td>
                <td style={tableCell}><span style={pill(p.payment_status === "paid" ? "#16a34a" : p.payment_status === "waived" ? "#7c3aed" : "#6b7280")}>{p.payment_status}</span></td>
                <td style={tableCell}>{(p.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td style={tableCell}>
                  {p.payment_status !== "waived" && (
                    <button style={{ ...btnGhost, padding: "4px 8px", fontSize: 10 }} onClick={() => { const r = window.prompt("Waive reason (required):"); if (r) billingAct("waive", { transaction_id: p.id, reason: r }, "Waive fee"); }} data-testid={`corp-waive-${p.id}`}>Waive</button>
                  )}
                </td>
              </tr>
            ))}
            {!(data.recent_payments || []).length && <tr><td colSpan={5} style={{ ...tableCell, color: "#94a3b8", textAlign: "center" }}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricsPage() {
  const [m, setM] = useState(null);
  const [rev, setRev] = useState(null);
  useEffect(() => {
    j("/api/iv/r10/corp/metrics").then(setM).catch(() => {});
    j("/api/iv/r10/corp/revenue").then(setRev).catch(() => {});
  }, []);
  if (!m || !rev) return <div style={{ ...card }}>Loading platform metrics…</div>;
  return (
    <div data-testid="corp-metrics">
      <div style={card}>
        <div style={subhead}>Platform Metrics</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
          <Stat label="Active Subscriptions" value={m.active_subscriptions} color="#16a34a" />
          <Stat label="Trial Accounts" value={m.trial_accounts} color="#f59e0b" />
          <Stat label="Total Users" value={m.total_users} />
          <Stat label="Jobs Today (ARM)" value={m.jobs_today} color={corpCyan} />
          <Stat label="IntelliVoice Subs" value={m.intellivoice_subscribers} color="#a855f7" />
          <Stat label="New Signups (Mo)" value={m.new_signups_this_month} />
          <Stat label="Churn Rate %" value={m.churn_rate_pct} color={m.churn_rate_pct > 5 ? corpAccent : "#16a34a"} />
          <Stat label="Trial → Paid %" value={m.trial_conversion_pct} />
          <Stat label="MRR (30d)" value={dollars(m.mrr_total_cents)} color={corpCyan} />
          <Stat label="Sync Errors (1h)" value={m.sync_errors_last_hour} color={m.sync_errors_last_hour ? corpAccent : "#16a34a"} />
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Revenue Month-over-Month</div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${m.revenue_mom?.length || 6}, 1fr)`, gap: 6, alignItems: "end", height: 140 }}>
          {(m.revenue_mom || []).map(mo => {
            const max = Math.max(...m.revenue_mom.map(x => x.amount_cents), 1);
            const h = Math.max(8, (mo.amount_cents / max) * 120);
            return (
              <div key={mo.month} style={{ textAlign: "center" }}>
                <div style={{ background: corpCyan, height: h, borderRadius: 3, marginBottom: 4 }}></div>
                <div style={{ fontSize: 9, color: "#94a3b8" }}>{mo.month.slice(5)}</div>
                <div style={{ fontSize: 10, color: "#e5e7eb" }}>{dollars(mo.amount_cents)}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Platform Revenue (this month)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
          <Stat label="Subscriptions" value={dollars(rev.subscription_revenue_cents)} color="#16a34a" />
          <Stat label="Lead Fees" value={dollars(rev.lead_fees_cents)} color="#1e40af" />
          <Stat label="Revenue Share" value={dollars(rev.revenue_share_cents)} color="#a855f7" />
          <Stat label="Combined" value={dollars(rev.combined_cents)} color={corpCyan} />
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={subhead}>Top 10 by Revenue</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={tableHead}>Company</th><th style={tableHead}>Amount</th></tr></thead>
            <tbody>
              {(rev.top_companies || []).map(t => (
                <tr key={t.company_id}><td style={tableCell}>{t.company_id}</td><td style={tableCell}>{dollars(t.amount_cents)}</td></tr>
              ))}
              {!(rev.top_companies || []).length && <tr><td colSpan={2} style={{ ...tableCell, color: "#94a3b8", textAlign: "center" }}>No revenue yet this month.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HealthPage() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => j("/api/iv/r10/corp/health").then(setH).catch(() => {});
    load(); const t = setInterval(load, 60000); return () => clearInterval(t);
  }, []);
  if (!h) return <div style={card}>Loading…</div>;
  const dot = (s) => ({ display: "inline-block", width: 12, height: 12, borderRadius: 999, background: s === "green" ? "#16a34a" : s === "yellow" ? "#f59e0b" : "#dc2626", marginRight: 6, verticalAlign: "middle" });
  return (
    <div data-testid="corp-health">
      <div style={card}>
        <div style={subhead}>Platform Health · auto-refresh 60s</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
          {["mongodb", "stripe", "resend", "twilio", "google_maps"].map(k => (
            <div key={k} style={{ background: "#0a0a0f", padding: 14, borderRadius: 6, border: "1px solid #2a2a3a" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 1, marginBottom: 6 }}>{k.toUpperCase()}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}><span style={dot(h[k])} />{h[k]?.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Last 24 Hours</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <Stat label="Sync Errors" value={h.sync_errors_24h} color={h.sync_errors_24h ? corpAccent : "#16a34a"} />
          <Stat label="Emails Sent" value={h.emails_24h?.sent || 0} />
          <Stat label="Emails Failed" value={h.emails_24h?.failed || 0} color={(h.emails_24h?.failed || 0) ? corpAccent : "#16a34a"} />
        </div>
      </div>
    </div>
  );
}

function AuditPage() {
  const [docs, setDocs] = useState([]);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (action) p.set("action", action);
    if (actor) p.set("actor", actor);
    j(`/api/iv/r10/corp/audit?${p.toString()}`).then(r => setDocs(r.audit || [])).catch(() => {});
  }, [action, actor]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="corp-audit">
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={subhead}>Master Audit Log · {docs.length}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="corp-audit-action" placeholder="action" value={action} onChange={e => setAction(e.target.value)} style={{ ...inputStyle, width: 150 }} />
            <input data-testid="corp-audit-actor" placeholder="actor email" value={actor} onChange={e => setActor(e.target.value)} style={{ ...inputStyle, width: 200 }} />
            <a href={`${API}/api/iv/r10/corp/audit/export.csv`} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="corp-audit-export">Export CSV</a>
          </div>
        </div>
      </div>
      <div style={card}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>When</th><th style={tableHead}>Actor</th><th style={tableHead}>Action</th><th style={tableHead}>Target</th><th style={tableHead}>Details</th></tr></thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id}>
                <td style={tableCell}>{(d.ts || "").slice(0, 19).replace("T", " ")}</td>
                <td style={tableCell}>{d.actor_email} <span style={{ ...pill("#1e40af"), marginLeft: 4 }}>{d.actor_role}</span></td>
                <td style={tableCell}><strong>{d.action}</strong></td>
                <td style={tableCell}>{d.target || "—"}</td>
                <td style={tableCell} title={JSON.stringify(d.details || {})}>{JSON.stringify(d.details || {}).slice(0, 60)}</td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={5} style={{ ...tableCell, color: "#94a3b8", textAlign: "center" }}>No audit entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnnouncementsPage() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("info");
  const [list, setList] = useState([]);
  const load = () => j("/api/iv/r10/corp/announcements").then(r => setList(r.announcements || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!subject || !body) return alert("Subject and body required");
    try {
      const r = await j("/api/iv/r10/corp/announcement", { method: "POST", body: JSON.stringify({ subject, body_html: body, body_text: body.replace(/<[^>]+>/g, ""), severity }) });
      alert(`Announced to ${r.companies_notified} companies`);
      setSubject(""); setBody(""); load();
    } catch (e) { alert(e.message); }
  };
  return (
    <div data-testid="corp-announce">
      <div style={card}>
        <div style={subhead}>Platform-wide Announcement</div>
        <input data-testid="corp-ann-subject" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} style={{ ...inputStyle, width: "100%", marginBottom: 6 }} />
        <textarea data-testid="corp-ann-body" placeholder="Body (plain text or HTML)" value={body} onChange={e => setBody(e.target.value)} style={{ ...inputStyle, width: "100%", minHeight: 100, fontFamily: "inherit", marginBottom: 6 }} />
        <select data-testid="corp-ann-severity" value={severity} onChange={e => setSeverity(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }}>
          <option value="info">Info</option><option value="warn">Warning</option><option value="err">Error</option>
        </select>
        <button style={btn} onClick={send} data-testid="corp-ann-send">Send to all subscribers</button>
      </div>
      <div style={card}>
        <div style={subhead}>Recent Announcements</div>
        {list.map(a => (
          <div key={a.id} style={{ padding: 8, borderBottom: "1px solid #2a2a3a", fontSize: 12 }}>
            <strong>{a.subject}</strong> <span style={pill(a.severity === "warn" ? "#f59e0b" : a.severity === "err" ? "#dc2626" : "#1e40af")}>{a.severity}</span>
            <div style={{ color: "#94a3b8", fontSize: 11 }}>{(a.ts || "").slice(0, 19).replace("T", " ")} · by {a.by}</div>
          </div>
        ))}
        {!list.length && <div style={{ color: "#94a3b8", fontSize: 12 }}>No announcements yet.</div>}
      </div>
    </div>
  );
}

// ─── Shell ────────────────────────────────────────────────────────────────
export function CorporateMasterApp() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("metrics");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem(CORP_TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    j("/api/iv/r10/corp/me").then(u => { setUser(u); setLoading(false); })
      .catch(() => { localStorage.removeItem(CORP_TOKEN_KEY); setLoading(false); });
  }, []);

  const logout = () => { localStorage.removeItem(CORP_TOKEN_KEY); setUser(null); };

  const nav = useMemo(() => [
    { id: "metrics", label: "Platform Metrics" },
    { id: "subscribers", label: "Subscribers" },
    { id: "audit", label: "Audit Log" },
    { id: "health", label: "Platform Health" },
    { id: "announce", label: "Announcements" },
  ], []);

  if (loading) return <div style={{ minHeight: "100vh", background: corpBg, color: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading corporate session…</div>;
  if (!user) return <CorpLogin onLogin={u => setUser(u)} />;

  return (
    <div data-testid="corp-shell" style={{ minHeight: "100vh", background: corpBg, color: "#e5e7eb", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <div style={{ background: "#000", borderBottom: "2px solid " + corpAccent, padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: 2, color: corpAccent }}>APEX INFINITY</span>
          <span style={{ fontSize: 10, letterSpacing: 3, color: "#94a3b8", marginLeft: 10 }}>· CORPORATE MASTER VIEW</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>{user.email} · {user.corp_role}</span>
          <button style={btnGhost} onClick={logout} data-testid="corp-logout">Sign Out</button>
        </div>
      </div>
      <div style={{ display: "flex", minHeight: "calc(100vh - 54px)" }}>
        <div style={{ width: 220, background: "#000", borderRight: "1px solid #2a2a3a", padding: 16 }}>
          {nav.map(n => (
            <button key={n.id} onClick={() => { setPage(n.id); setSelectedCompany(null); }} data-testid={`corp-nav-${n.id}`}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", background: page === n.id ? corpAccent : "transparent", color: "#e5e7eb", border: "none", borderRadius: 4, marginBottom: 4, cursor: "pointer", fontSize: 13 }}>
              {n.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, padding: 24, overflow: "auto" }}>
          {selectedCompany
            ? <CompanyDetail companyId={selectedCompany} onBack={() => setSelectedCompany(null)} />
            : page === "metrics" ? <MetricsPage />
            : page === "subscribers" ? <SubscribersPage onView={(id) => setSelectedCompany(id)} />
            : page === "audit" ? <AuditPage />
            : page === "health" ? <HealthPage />
            : page === "announce" ? <AnnouncementsPage />
            : null}
        </div>
      </div>
    </div>
  );
}

// Detection helper — used by index.js / App.js entry
export function shouldRenderCorporate() {
  if (typeof window === "undefined") return false;
  return window.location.hash === "#corporate" || (window.location.host || "").startsWith("corporate.");
}
